import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import Email from "app/pages/.shared/profile/Email";
import Separator from "app/pages/.shared/Separator";
import "./PaymentContact.scss";
import LoadingPlaceholder from "app/pages/.shared/LoadingPlaceholder";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";
import IdentityFieldset from "app/pages/.shared/profile/IdentityFieldset";
import AddressFieldset from "app/pages/.shared/profile/AddressFieldset";
import { useFormikContext } from "formik";
import PhoneIntl from "app/pages/.shared/profile/PhoneIntl";
import FormErrorMessages from "app/pages/.shared/form/FormErrorMessages/FormErrorMessages";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";

const PaymentContact = ({ isLoading, shop, shouldDisableEmailField }) => {
	const {
		setFieldValue,
		setFieldTouched,
		errors = {},
		setFieldError,
		isSubmitting,
		submitCount,
	} = useFormikContext();
	const { track } = useAnalytics();
	const ref = useRef();
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: false });

	useEffect(() => {
		if (isElementOnScreen && !isLoading) {
			track("payment_view_contact");
		}
	}, [isElementOnScreen, isLoading]);

	return (
		<div className="payment-contact" ref={ref}>
			{isLoading ? (
				<LoadingPlaceholder
					message={<FormattedMessage id="payment.contact.loading.text" />}
				/>
			) : (
				<>
					<FormErrorMessages
						errors={errors}
						fieldsFilter={["contact"]}
						isSubmitting={isSubmitting}
						submitCount={submitCount}
						message={<FormattedMessage id="error.booking.payment.fields.required" />}
					/>

					<Typography
						variant={TYPOGRAPHY_VARIANTS.LARGE}
						isBold
						className="payment__subtitle"
						component="h3"
					>
						<FormattedMessage id="payment.contact.contact" />
					</Typography>

					<IdentityFieldset isRequired={true} blocPrefixName="contact" />

					<div className="payment__email-phone">
						{shouldDisableEmailField ? (
							<Email disabled blocPrefixName="contact" />
						) : (
							<Email blocPrefixName="contact" />
						)}
						<PhoneIntl
							id="phone"
							name="contact.phone"
							shop={shop}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
							setFieldError={setFieldError}
							label={<FormattedMessage id="phone.label" />}
							data-testid="phone-input"
						/>
					</div>

					<Separator design="middle" />

					<Typography
						variant={TYPOGRAPHY_VARIANTS.LARGE}
						isBold
						className="payment__subtitle"
						component="h3"
					>
						<FormattedMessage id="payment.contact.address" />
					</Typography>

					<AddressFieldset
						isRequired
						setFieldValue={setFieldValue}
						blocPrefixName="contact"
						setFieldError={setFieldError}
					/>
				</>
			)}
		</div>
	);
};

PaymentContact.propTypes = {
	shop: PropTypes.string,
	shouldDisableEmailField: PropTypes.bool,
	isLoading: PropTypes.bool,
};

export default PaymentContact;
